import {
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Radio,
  RadioGroup,
  SimpleGrid,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';

type Option = {
  value: string;
  label: string;
};

export interface CheckboxGroupFieldProps {
  label?: React.ReactNode;
  name: string;
  options: Option[];
  isDisabled?: boolean;
  error?: string;
  value?: string[];
  helpers?: any;
  field?: any;
  meta?: any;
  fontWeight?: string;
  fontSize?: string;
  mb?: string | number;
  labelFontSize?: string;
  itemsPerRow?: number;
  spacing?: number;
  labelMb?: number | string;
  touched?: boolean;
  maxOptions?: number;
  [x: string]: any;
}

export const CheckboxGroupField: React.FC<CheckboxGroupFieldProps> = ({
  label,
  name,
  options,
  error,
  value,
  helpers,
  field,
  fontWeight,
  fontSize,
  mb,
  isDisabled,
  labelFontSize,
  itemsPerRow = 4,
  spacing = 4,
  labelMb,
  touched,
  maxOptions,
  onDark,
  ...rest
}) => {
  useEffect(() => {
    if (maxOptions && value && value.length > maxOptions && helpers && helpers.setValue) {
      helpers.setValue(value.slice(0, maxOptions));
    }
  }, [maxOptions, value, helpers]);

  const handleCheckboxChange = (values: string[]) => {
    if (helpers && helpers.setValue) {
      if (maxOptions && values.length > maxOptions) {
        const newValue = values.slice(-maxOptions);
        helpers.setValue(newValue);
      } else {
        helpers.setValue(values);
      }
    }
  };

  const handleRadioChange = (newValue: string) => {
    if (helpers && helpers.setValue) {
      helpers.setValue([newValue]);
    }
  };

  const isTouched = touched === undefined ? false : Boolean(touched);
  const showError = !!error && isTouched;

  const [lightColor, darkColor] = onDark
    ? ['white', 'chakra-body-text']
    : ['chakra-body-text', 'white'];

  const textColorPrimary = useColorModeValue(lightColor, darkColor);

  if (maxOptions === 1) {
    return (
      <FormControl isInvalid={!!error} mb={mb || 4}>
        {label && (
          <FormLabel htmlFor={name} fontSize={labelFontSize} mb={labelMb} fontWeight="bold">
            {label}
          </FormLabel>
        )}
        <RadioGroup
          value={value && value.length > 0 ? value[0] : ''}
          onChange={handleRadioChange}
          colorScheme="primary"
        >
          <SimpleGrid columns={[1, 2, Math.min(itemsPerRow, 3), itemsPerRow]} spacing={spacing}>
            {options.map((option) => (
              <Radio key={option.value} value={option.value} isDisabled={isDisabled} {...rest}>
                <Text fontWeight={fontWeight} fontSize={fontSize} color={textColorPrimary}>
                  {option.label}
                </Text>
              </Radio>
            ))}
          </SimpleGrid>
        </RadioGroup>
        {showError && <FormErrorMessage>{error}</FormErrorMessage>}
      </FormControl>
    );
  }

  return (
    <FormControl isInvalid={!!error} mb={mb || 4}>
      {label && (
        <FormLabel htmlFor={name} fontSize={labelFontSize} mb={labelMb} fontWeight="bold">
          {label}
        </FormLabel>
      )}
      <CheckboxGroup value={value || []} onChange={handleCheckboxChange} colorScheme="primary">
        <SimpleGrid columns={[1, 2, Math.min(itemsPerRow, 3), itemsPerRow]} spacing={spacing}>
          {options.map((option) => {
            const isOptionDisabled =
              isDisabled ||
              (!!maxOptions &&
                value &&
                value.length >= maxOptions &&
                !value.includes(option.value));

            return (
              <Checkbox
                key={option.value}
                value={option.value}
                isDisabled={isOptionDisabled}
                {...rest}
              >
                <Text fontWeight={fontWeight} fontSize={fontSize} color={textColorPrimary}>
                  {option.label}
                </Text>
              </Checkbox>
            );
          })}
        </SimpleGrid>
      </CheckboxGroup>
      {showError && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};
